import {
	AfterViewInit,
	ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Inject, OnInit, ViewChild, ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatSidenav } from '@angular/material/sidenav';
import { Observable, combineLatest } from 'rxjs';
import { debounceTime, filter, take, tap } from 'rxjs/operators';
import { ToastContainerDirective } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AbstractComponent } from '@ap-angular/shared/abstract';
import { isNullOrEmpty, ModalUtils } from '@ap-angular/shared/utils';
import { PAGE } from '@ap-angular/shared/schema';
import { ApplicationService, MEDIA_QUERY_SIZE, MediaQuerySizeType } from '@ap-angular/service/application';
import { DialogService, DialogWidgetComponent } from '@ap-angular/service/dialog';
import { MessageService } from '@ap-angular/service/message';
import { ProviderService } from '@ap-angular/service/provider';
import { UserService } from '@ap-angular/service/user';
import { PersonDTO } from '@auto-generated/ap-platform/care-providers';
import { NotificationDTO, NotificationTypeEnumDTO, UserRoleEnumDTO } from '@auto-generated/ap-platform/users';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MatButton } from '@angular/material/button';
import { AnalyticsConstants, AnalyticsService } from '@ap-angular/service/analytics';

@Component({
	selector: 'ap-provider-template',
	templateUrl: './template.component.html',
	styleUrls: ['./template.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProviderTemplateComponent extends AbstractComponent implements OnInit, AfterViewInit {

	@HostBinding('class') class = 'template';

	@ViewChild('sidenav', {static: true}) public sidenav: MatSidenav;

	@ViewChild(ToastContainerDirective, {static: true}) toastContainer: ToastContainerDirective;

	@ViewChild('headerUser') public headerUser: MatButton;

	public readonly PAGE = PAGE;

	public permission$ = this._userService.getPermission$().pipe(
		/** Double check to be sure we remove loading from user */
		tap(() => this._userService.loading(false))
	);

	public name$: Observable<string> = this._userService.getAccountName$();

	public email$: Observable<string> = this._userService.getAccountEmail$();

	public countUnreadMessage$: Observable<number> = this._providerService.readCountUnreadMessage$();

	public provider$: Observable<PersonDTO> = this._providerService.readProvider$();

	public env: { version: string, production: string, features: { reporting: boolean } };

	public employeeLimitedAccess: boolean;

	public activeRoute: string;

	public activeSubmenu: Array<boolean> = [false, false];

	public newsList: Array<NotificationDTO>;

	public showSearchButton: boolean;

	constructor(
		@Inject('ENVIRONMENT') private readonly _environment: any,
		private readonly _applicationService: ApplicationService,
		private readonly _changeDetectorRef: ChangeDetectorRef,
		private readonly _dialogService: DialogService,
		private readonly _messageService: MessageService,
		private readonly _providerService: ProviderService,
		private readonly _route: ActivatedRoute,
		private readonly _router: Router,
		private readonly _userService: UserService,
		private readonly _translateService: TranslateService,
		private readonly _matIconRegistry: MatIconRegistry,
		private readonly _domSanitizer: DomSanitizer,
		private readonly _analyticsService: AnalyticsService
	) {
		super();
		this.env = {version: this._environment.version, production: this._environment.production, features: {
        reporting: this._environment.features.reporting
      }};
		this._changeDetectorRef.markForCheck();
	}

	public ngOnInit() {
		this._setActiveRoute();

		this._userService.loadNotificationList();
		super.addSubscription(this._userService.getNotificationList$()
			.subscribe((next) => {
				this.newsList = next.filter((item) => item.type === NotificationTypeEnumDTO.NEWRELEASENOTES);
				this._changeDetectorRef.markForCheck();
			})
		);
		if (this._userService.isRole(UserRoleEnumDTO.EMPLOYEE)) {
			super.addSubscription(this._userService.readManageResource$()
				.subscribe((next) => this.employeeLimitedAccess = isNullOrEmpty(next))
			);
		}

		super.addSubscription(this._router.events
			.pipe(
				filter(event => event instanceof NavigationEnd && !!this._router.getCurrentNavigation().finalUrl)
			).subscribe(() => {
				this._setActiveRoute();
			})
		);

		super.addSubscription(
			combineLatest([this._route.url, this._applicationService.getMediaQuerySize$()])
				.subscribe(([_, querySize]) => {
					this._setTopBarButtons(querySize);
				})
		)

		this._matIconRegistry.addSvgIcon('ap-help-icon', this._domSanitizer.bypassSecurityTrustResourceUrl('./assets/help-icon.svg'));
		this._matIconRegistry.addSvgIcon('ap-messages-icon', this._domSanitizer.bypassSecurityTrustResourceUrl('./assets/messages-icon.svg'));
		this._matIconRegistry.addSvgIcon('ap-waiting-list-icon', this._domSanitizer.bypassSecurityTrustResourceUrl('./assets/waiting-list-icon.svg'));
	}

	public ngAfterViewInit(): void {
		super.addSubscription(combineLatest([this._applicationService.getMediaQuerySize$(), this.name$]).pipe(debounceTime(100)).subscribe(([querySize, name]) => {
			const smallerThenLarge = querySize === MEDIA_QUERY_SIZE.LARGE || querySize === MEDIA_QUERY_SIZE.SMALL || querySize === MEDIA_QUERY_SIZE.MEDIUM;

			if(smallerThenLarge) this._applicationService.setPositionOfSearchInput(0);
			else this._applicationService.setPositionOfSearchInput( this.headerUser._getHostElement().offsetWidth);
		}))
	}

	public widget(data: {}) {
		this._dialogService.openDialog(DialogWidgetComponent, {...ModalUtils.config(), data: data})
			.componentInstance
			.onToast
			.subscribe((toast) => {
				if (toast.type === 'success') {
					this._messageService.toastSuccess(this._translateService.instant(toast.content));
				} else if (toast.type === 'error') {
					this._messageService.toastError(this._translateService.instant(toast.content));
				}
			});
	}

	public logout(): void {
		this._analyticsService.emitEvent(AnalyticsConstants.ADMIN_APP_CATEGORY, AnalyticsConstants.ACTION_LOGOUT);
		this._userService.logout();
	}

	public goToReleasePage(): void {
		if (isNullOrEmpty(this.newsList)) {
			return;
		}
		this._router.navigate([PAGE.PROVIDER_NEWS.fullpath]).then(() =>
			super.addSubscription(this._userService.markNotificationAsRead$(this.newsList[0].id)
				.pipe(take(1))
				.subscribe(() => this._setActiveRoute())
			)
		);
	}

	public searchClicked(event$: Event): void {
		event$.stopPropagation();
		const path = this._route.snapshot.firstChild.url[0].path;
		 this._applicationService.setShowSearch(true);
	}

	private _setTopBarButtons(querySize: MediaQuerySizeType): void {
		const path = this._route.snapshot.firstChild.url[0].path;
		const showOnMediumAndDown = querySize === MEDIA_QUERY_SIZE.LARGE || querySize === MEDIA_QUERY_SIZE.SMALL || querySize === MEDIA_QUERY_SIZE.MEDIUM;
		this.showSearchButton = showOnMediumAndDown && (path === PAGE.PROVIDER_CALENDAR.path || path === PAGE.PROVIDER_PATIENT.path);
	}

	private _setActiveRoute(): void {
		switch (this._route.snapshot.firstChild.url[0].path) {
			case PAGE.PROVIDER_PROFILE.path:
			case PAGE.PROVIDER_RESOURCE.path:
			case PAGE.PROVIDER_TREATMENT.path:
				this.activeSubmenu[0] = true;
				this.activeRoute = 'profile';
				break;
			case PAGE.FAQ.path:
			case PAGE.CONTACT.path:
			case PAGE.LEGAL.path:
			case PAGE.PROVIDER_NEWS.path:
				this.activeSubmenu[1] = true;
				this.activeRoute = 'help';
				break;
			default:
				this.activeRoute = undefined;
				break;
		}
	}
}
